// Definiciones de tours guiados para el admin (driver.js).
//
// Por qué viven acá y NO en la DB:
//   Los selectores `data-tour` están atados al markup. Si los pasos
//   vivieran en DB, refactorear el JSX implicaría sincronizar dos
//   fuentes de verdad — los selectores se desincronizarían en silencio
//   cuando alguien renombra un componente. El contenido editorial
//   editable es la FAQ (vive en D1). El tour es código.
//
// Estructura:
//   - `globalTour`: tour del sidebar + bienvenida. Auto-arranca la
//     primera vez que un user nuevo entra a /admin/*.
//   - `tourByPath`: map de pathname → steps específicos de esa página.
//     Si no hay entrada, el botón "?" solo muestra la FAQ.
//
// Selectores: usar `[data-tour="key"]` para desacoplar del DOM. Las
// clases utility de Tailwind cambian con cada refactor; data-tour es
// estable.

import type { Config, DriveStep } from "driver.js";
import type { Role } from "@juegossc/shared";

// Tour global — corre 1 vez (flag localStorage `juegossc:tour-seen`).
// Cubre los elementos del sidebar que están presentes en TODAS las
// pantallas admin: navegación, selector de sucursal, perfil.
//
// Por qué es una FUNCIÓN y no const: el contenido depende del rol del
// usuario. Para admins comunes NO se muestra el step del selector de
// sucursal (ese elemento ni siquiera existe en su sidebar) y NUNCA se
// menciona "super_admin" en el texto. Para super_admin el step se
// incluye con un texto natural sobre el selector.
export function buildGlobalTour(role: Role | null): DriveStep[] {
  const isSuper = role === "super_admin";
  const steps: DriveStep[] = [
    {
      popover: {
        title: "¡Bienvenido al panel de JuegosSC!",
        description:
          "Te muestro las partes principales del admin en menos de un minuto. Podés saltearlo o volverlo a ver cuando quieras desde el botón <strong>?</strong> arriba a la derecha.",
      },
    },
    {
      element: '[data-tour="sidebar-nav"]',
      popover: {
        title: "Menú principal",
        description:
          "Acá tenés todo lo que podés hacer sobre tu sucursal: inicio, premios, jugadas y la configuración de tu pantalla.",
        side: "right",
        align: "start",
      },
    },
  ];

  // Step del selector de sucursal — SOLO si el rol lo ve. Para admins
  // comunes el elemento [data-tour="sidebar-sucursal"] no existe (su
  // sidebar lo oculta), y aparte el texto histórico mencionaba
  // "super_admin" — sacar el step entero evita ambas filtraciones.
  if (isSuper) {
    steps.push({
      element: '[data-tour="sidebar-sucursal"]',
      popover: {
        title: "Sucursal activa",
        description:
          "Acá elegís sobre qué sucursal estás operando. Cambiar acá refresca todo el panel con los datos de esa sucursal.",
        side: "right",
        align: "start",
      },
    });
  }

  steps.push(
    {
      element: '[data-tour="user-menu"]',
      popover: {
        title: "Tu perfil",
        description:
          "Tu email, tu rol y la opción de cerrar sesión están acá. Hacé click sobre el avatar para abrir el menú.",
        side: "right",
        align: "start",
      },
    },
    {
      element: '[data-tour="help-button"]',
      popover: {
        title: "Botón de ayuda",
        description:
          "Cuando tengas dudas, este botón abre la ayuda con tour por página y FAQ con buscador.",
        side: "left",
      },
    },
  );

  return steps;
}

// Conjunto de paths solo accesibles para super_admin. Si un admin
// común entra (escribiendo URL manual), el AdminLayout lo redirige a
// /admin. Acá lo usamos para NO devolver tour de esas páginas a
// admins comunes (defensa adicional al routing guard).
export const SUPER_ONLY_PATHS = new Set([
  "/admin/organizacion",
  "/admin/campanas",
  "/admin/shopping",
  "/admin/faqs",
]);

// Tour por página — devuelve los steps para el rol dado. Si el path es
// super-only y el rol no es super_admin, devuelve null (sin tour).
export function buildTourByPath(
  pathname: string,
  role: Role | null,
): DriveStep[] | null {
  const isSuper = role === "super_admin";
  if (SUPER_ONLY_PATHS.has(pathname) && !isSuper) return null;
  return tourByPath[pathname] ?? null;
}

// Tour por página — DriveStep[] con selectores específicos del view.
// `null` significa que esa página no tiene tour propio (solo FAQ).
const tourByPath: Record<string, DriveStep[] | null> = {
  // Inicio: alertas + stock por agotarse + actividad + accesos rapidos.
  // El step de stock solo aparece cuando hay productos por agotarse (la
  // seccion se oculta si no), pero driver.js saltea en silencio un step
  // cuyo elemento no existe, asi que dejarlo no rompe nada.
  "/admin": [
    {
      popover: {
        title: "Inicio",
        description:
          "Tu panel de estado: alertas para resolver, stock por agotarse, actividad reciente y accesos rápidos.",
      },
    },
    {
      element: '[data-tour="dash-alertas"]',
      popover: {
        title: "Alertas",
        description:
          "Avisos accionables de la sucursal: PIN sin configurar, modo prueba activo o ningún juego habilitado. Cada uno te lleva a donde se arregla.",
      },
    },
    {
      element: '[data-tour="dash-stock"]',
      popover: {
        title: "Stock por agotarse",
        description:
          "Premios activos con stock bajo (3 o menos). Los que están en cero salen en rojo: si salen sorteados, el juego se queda sin premio para entregar.",
      },
    },
    {
      element: '[data-tour="dash-metricas"]',
      popover: {
        title: "Actividad",
        description:
          "Jugadas de las últimas 24 horas y de los últimos 7 días, con cuántas fueron \"seguí participando\".",
      },
    },
    {
      element: '[data-tour="dash-accesos"]',
      popover: {
        title: "Accesos rápidos",
        description:
          "Atajos a las tareas frecuentes: premios, jugadas y tu pantalla.",
      },
    },
  ],
  "/admin/productos": [
    {
      popover: {
        title: "Premios",
        description:
          "Un premio es un objeto físico. Vive una vez con stock único y participa en uno o más juegos (ruleta, raspadita, memoria).",
      },
    },
    {
      element: '[data-tour="productos-new"]',
      popover: {
        title: "Crear producto",
        description:
          "Nombre, color, stock (opcional) y en qué juegos aparece. El peso define la probabilidad relativa dentro de cada juego.",
      },
    },
    {
      element: '[data-tour="productos-list"]',
      popover: {
        title: "Lista de productos",
        description:
          "Cada fila muestra categoría (real / consolation / reintento), stock y chips por juego. Las acciones permiten editar, fusionar duplicados y borrar.",
      },
    },
  ],
  "/admin/ganadores": [
    {
      popover: {
        title: "Jugadas",
        description:
          "Registro histórico de cada jugada (premios, consolation y reintentos). Acá marcás los premios como entregados.",
      },
    },
    {
      element: '[data-tour="ganadores-filters"]',
      popover: {
        title: "Filtros",
        description:
          "Filtrá por fecha, juego, premio o estado de entrega para encontrar una jugada específica.",
      },
    },
    {
      element: '[data-tour="ganadores-export"]',
      popover: {
        title: "Exportar CSV",
        description:
          "Descargá el listado filtrado para abrir en Excel. Útil para reportes mensuales.",
      },
    },
    {
      element: '[data-tour="ganadores-list"]',
      popover: {
        title: "Lista de jugadas",
        description:
          "Cada fila tiene un botón \"Marcar entregado\" cuando todavía no se entregó el premio. Queda registrado quién y cuándo.",
      },
    },
  ],
  // Mi pantalla: 4 pestañas (Kiosco y seguridad / Datos del cliente /
  // Juegos / Sonidos). Los anchors config-juegos y config-sonidos viven
  // en pestañas que arrancan ocultas (display:none) — driver.js igual los
  // ubica pero sin highlight visible. Por eso el step de Juegos describe
  // la pestaña en vez de resaltar un control puntual, y el de modo prueba
  // sí resalta porque "Kiosco y seguridad" es la pestaña por defecto.
  "/admin/pantalla": [
    {
      popover: {
        title: "Mi pantalla",
        description:
          "Ajustes que afectan SOLO a tu sucursal, ordenados en pestañas: Seguridad, Datos del cliente, Juegos y Sonidos.",
      },
    },
    {
      element: '[data-tour="config-modo-prueba"]',
      popover: {
        title: "Modo prueba",
        description:
          "En la pestaña \"Seguridad\". Cuando lo encendés, las jugadas se marcan como prueba y NO cuentan en el Inicio. Útil antes de un evento para chequear que todo funciona.",
      },
    },
    {
      element: '[data-tour="config-juegos"]',
      popover: {
        title: "Juegos",
        description:
          "En la pestaña \"Juegos\" activás ruleta, raspadita y/o memoria y configurás cada uno. Cada juego tiene una previsualización a la derecha que se actualiza con lo que vas cambiando. Solo los activos aparecen en la pantalla del cliente.",
      },
    },
  ],
  // Organizacion unifica Sucursales + Usuarios en pestañas internas. El
  // tour abre describiendo ambas; el step de "Nueva sucursal" apunta a
  // la pestaña por defecto (Sucursales). El anchor de "Alta de usuario"
  // solo existe con esa pestaña activa, por eso no lo incluimos: driver
  // necesita el elemento presente en pantalla.
  "/admin/organizacion": [
    {
      popover: {
        title: "Organización",
        description:
          "Acá administrás las sucursales y los usuarios del sistema. Cambiá entre ambas con las pestañas de arriba.",
      },
    },
    {
      element: '[data-tour="sucursales-new"]',
      popover: {
        title: "Crear sucursal",
        description:
          "Nombre, slug único (URL-safe) y ciudad. Crear una sucursal genera automáticamente sus settings iniciales. Después asignás un admin desde la pestaña Usuarios.",
      },
    },
  ],
  "/admin/campanas": [
    {
      popover: {
        title: "Campañas",
        description:
          "Cada campaña es una imagen + nombre que se muestra en todas las sucursales (ej: Mundial, Navidad). Solo una activa a la vez.",
      },
    },
    {
      element: '[data-tour="campanas-new"]',
      popover: {
        title: "Crear campaña",
        description:
          "Subí la imagen y guardá. Después la activás con el botón \"Activar\". Cambio instantáneo, sin deploy.",
      },
    },
  ],
  "/admin/shopping": [
    {
      popover: {
        title: "Ajustes del shopping",
        description:
          "Configuración visual y sonora que se aplica a TODAS las sucursales: logo, tipografías, fondos, íconos, campaña y sonidos.",
      },
    },
    {
      element: '[data-tour="global-tabs"]',
      popover: {
        title: "Pestañas",
        description:
          "Pantalla principal (logo, tipografías, fondos, íconos de los juegos, campaña y distribución del menú), Pantalla de juego y Sonidos (estilos + archivos por evento). A la derecha tenés una previsualización vertical de la pantalla real que ocupa todo el alto y refleja lo que vas cambiando.",
      },
    },
  ],
  "/admin/faqs": [
    {
      popover: {
        title: "FAQ del admin",
        description:
          "Acá editás las preguntas frecuentes que aparecen en el modal de ayuda (botón ?). Lo que escribís acá lo ven TODOS los admins.",
      },
    },
    {
      element: '[data-tour="faqs-new"]',
      popover: {
        title: "Nueva pregunta",
        description:
          "Pregunta + respuesta + categoría + orden de aparición. Las categorías \"globales\" solo las ven los administradores con acceso a esas páginas.",
      },
    },
  ],
};

// Config global del driver — colores que respetan el tema oscuro
// violeta del admin. Aplica a todos los tours.
export const driverConfig: Config = {
  showProgress: true,
  allowClose: true,
  smoothScroll: true,
  stagePadding: 6,
  stageRadius: 8,
  popoverClass: "juegossc-driver-popover",
  progressText: "Paso {{current}} de {{total}}",
  nextBtnText: "Siguiente →",
  prevBtnText: "← Anterior",
  doneBtnText: "Listo",
};

// Helper localStorage para "ya vi el tour global".
const STORAGE_KEY = "juegossc:tour-seen";

export function hasSeenGlobalTour(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function markGlobalTourSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    // ignorar (privacy mode)
  }
}

export function resetGlobalTourSeen(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignorar
  }
}
